/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from 'mobx'
import axios, { AxiosError } from 'axios'
import { OrdersService } from '~/shared/api/services/orders'
import {
  Orders,
  Filters,
  Loaders,
  EditableOrdertModel,
  Infos,
  HistoryArray,
  Items,
  DigitalProducts,
  Related,
  ProductsArray,
  StampProductsArray,
  Shipping,
  Productavailable,
  Attachments,
  OrdersInWork,
  OrderInWorkActions,
} from '~/shared/api/services/orders/types'
import { ProductsService, ShippingModel } from '~/shared/api'
import { StoresService } from '~/shared/api/services/stores'
import {
  Countries,
  Currencie,
  PaymentSystem,
  Regions,
} from '~/shared/api/services/stores/types'
import {
  AvailableProductModel,
  Product,
} from '~/shared/api/services/products/types'
import {
  FilterItem,
  FiltersBySelect,
  Store,
} from '~/shared/api/services/global/types'
import { store } from '~/app/store'
import { RetailService } from '~/shared/api/services/retail'
import { handleAxiosError } from '~/shared/lib'

export class OrdersModel {
  [key: string]: any

  ordersAbortController: AbortController | null = null

  tempID = ''

  ordersError = false

  error404 = false

  infos: Infos | null = null

  history: HistoryArray = []

  related: Related[] = []

  loaders: Loaders = {
    orders: false,
    loadMoreOrders: false,
    shipping: false,
    store: false,
    payment: false,
    products: false,
    newDocument: false,
    reshipOrder: false,
    date: false,
    comment: false,
    editRecipient: false,
    shippingByProducts: false,
    availabilitesProducts: false,
    ordersDemandProductVariationId: false,
    paymentCheck: false,
    assembly: false,
    dispatch: false,
    itemsRequired: false,
    shippedLastDay: false,
    removeProductFromOrder: false,
    addProductForOrder: false,
    cancelOrder: false,
    storeComment: false,
    availabilitesProductOptions: false,
  }

  availabilitesProducts: AvailableProductModel[] = []

  availabilitesProductOptions: Productavailable | null = null

  orders: Orders[] | null = null

  items: Items[] = []

  digitalProducts: DigitalProducts[] = []

  currentOrder: EditableOrdertModel | null = null

  currentOrderProducts: ProductsArray = []

  currentOrderStampProducts: StampProductsArray = []

  currencies: Currencie[] = []

  attachments: Attachments[] = []

  ordersInWorkPaymentCheck: OrdersInWork[] = []

  ordersInWorkAssembly: OrdersInWork[] = []

  ordersInWorkDispatch: OrdersInWork[] = []

  ordersInWorkItemsRequired: OrdersInWork[] = []

  ordersInWorkShippedLastDay: OrdersInWork[] = []

  updatedStoreErrors: { status: boolean; updeter: string }[] = []

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  filters: Filters = {
    state: [
      'created',
      'assembly',
      'dispatch',
      'shipped',
      'payment invoice',
      'cancel',
      'completed',
    ],
    products: [],
    store: [],
    shipping: [],
    payment: [],
    orderNumber: '',
    customerName: '',
    customerLastName: '',
    customerEmail: '',
    marketplaceId: '',
    ordersQ: '',
    date: [],
    countries: [],
  }

  ordersDemandProductVariationId: Orders[] = []

  demandProductVariationRequestId = 0

  productsForFilter: Product[] = []

  originalProductsForChangedFilter: FilterItem[] = []

  shipping: ShippingModel[] = []

  shippingByProducts: Shipping[] = []

  store: Store[] = []

  payment: PaymentSystem[] = []

  state = [
    { name: 'payment', label: 'Payment' },
    { name: 'created', label: 'Created' },
    { name: 'assembly', label: 'Assembly' },
    { name: 'dispatch', label: 'Dispatch' },
    { name: 'shipped', label: 'Shipped' },
    { name: 'checkout', label: 'Checkout' },
    { name: 'payment invoice', label: 'Payment invoice' },
    { name: 'cancel', label: 'Cancel' },
    { name: 'failed', label: 'Failed' },
    { name: 'completed', label: 'Completed' },
  ]

  countries: Countries[] = []

  regions: Regions[] = []

  q = ''

  changedTab = 'listOfOrders'

  constructor() {
    makeAutoObservable(this, { ordersAbortController: false })
  }

  setTempID = (id: string) => {
    this.tempID = id
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  resetCurrentOrder = () => {
    this.currentOrder = null
    this.items = []
    this.digitalProducts = []
    this.currentOrderProducts = []
    this.currentOrderStampProducts = []
  }

  getShippings = async () => {
    try {
      this.activeLoader('shipping')
      const { data } = await ProductsService.getShipping()
      runInAction(() => {
        this.shipping = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('shipping')
      })
    }
  }

  getShippingMethodsByProducts = async () => {
    this.activeLoader('shippingByProducts')
    try {
      if (
        this.currentOrder?.storeId &&
        this.currentOrder?.items?.length !== 0
      ) {
        const { data } = await OrdersService.getShippingMethodsByProducts({
          storeId: this.currentOrder.storeId,
          region: this.currentOrder.recipient.countryZone as string,
          products: this.currentOrderProducts,
        })
        runInAction(() => {
          this.shippingByProducts = data
        })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('shippingByProducts')
      })
    }
  }

  getCurrencies = async () => {
    try {
      if (this.currentOrder?.storeId) {
        const { data } = await OrdersService.getCurrencies(
          this.currentOrder.storeId,
        )
        runInAction(() => {
          this.currencies = data
        })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('shippingByProducts')
      })
    }
  }

  getPayments = async () => {
    try {
      this.activeLoader('payment')
      const { data } = await StoresService.getPaymentSystem()
      runInAction(() => {
        this.payment = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('payment')
      })
    }
  }

  getInfos = async (id: string) => {
    try {
      const { data } = await OrdersService.getInfos(id)
      runInAction(() => {
        this.infos = data
      })
    } catch (error) {
      throw new Error(handleAxiosError(error))
    } finally {
      this.closeLoader(loader)
    }
  }

  getHistory = async (id: string) => {
    try {
      const { data } = await OrdersService.getHistory(id)
      runInAction(() => {
        this.history = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getRelated = async (id: string) => {
    try {
      const { data } = await OrdersService.getRelated(id)
      runInAction(() => {
        this.related = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getCountries = async () => {
    try {
      const { data } = await StoresService.getCountries()
      runInAction(() => {
        this.countries = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getRegions = async (newCountry?: string) => {
    try {
      if (this.currentOrder) {
        const { data } = await StoresService.getRegions(
          newCountry || this.currentOrder.recipient.country,
        )
        runInAction(() => {
          this.regions = data
        })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getProducts = async () => {
    try {
      this.activeLoader('products')
      const params = this.q ? { params: { q: this.q } } : {}
      const { data } = await ProductsService.getProducts(params)
      runInAction(() => {
        this.productsForFilter = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('products')
      })
    }
  }

  getAvailabilitesProducts = async (
    explicitStoreId?: string,
    searchQuery?: string,
  ) => {
    try {
      this.activeLoader('availabilitesProducts')
      const storeId = explicitStoreId || this.currentOrder?.storeId

      if (storeId) {
        const trimmedSearchQuery = searchQuery?.trim()
        const { data } = await ProductsService.getAvailableProducts({
          params: {
            limit: 20,
            offset: 0,
            storeId,
            ...(trimmedSearchQuery ? { q: trimmedSearchQuery } : {}),
          },
        })
        runInAction(() => {
          this.availabilitesProducts = data
        })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('availabilitesProducts')
      })
    }
  }

  getDemandProductVariationId = async (
    id: string,
    demand: boolean,
    orderNumber?: string,
  ) => {
    this.demandProductVariationRequestId += 1
    const requestId = this.demandProductVariationRequestId
    this.ordersDemandProductVariationId = []
    this.activeLoader('ordersDemandProductVariationId')
    const params = {
      limit: 20,
      offset: 0,
      orderNumber,
    } as {
      limit: number
      offset: number
      orderNumber?: string
      itemProductVariationId?: string
      state?: string[]
      demandProductVariationId?: string
    }
    if (demand) {
      params.itemProductVariationId = id
      params.state = ['created', 'assembly']
    } else {
      params.demandProductVariationId = id
    }
    if (!orderNumber) {
      delete params.orderNumber
    }
    try {
      const { data } = await OrdersService.getDemandProductVariationId({
        params,
      })

      if (requestId === this.demandProductVariationRequestId) {
        runInAction(() => {
          this.ordersDemandProductVariationId = data
        })
      }
      return data
    } finally {
      if (requestId === this.demandProductVariationRequestId) {
        this.closeLoader('ordersDemandProductVariationId')
      }
    }
  }

  clearDemandProductVariationId = () => {
    this.demandProductVariationRequestId += 1
    this.ordersDemandProductVariationId = []
    this.closeLoader('ordersDemandProductVariationId')
  }

  getOrdersInWork = async (action: OrderInWorkActions) => {
    const storeOrdersInWorkActions = {
      paymentCheck: 'ordersInWorkPaymentCheck',
      assembly: 'ordersInWorkAssembly',
      dispatch: 'ordersInWorkDispatch',
      itemsRequired: 'ordersInWorkItemsRequired',
      shippedLastDay: 'ordersInWorkShippedLastDay',
    }

    this.activeLoader(action)
    try {
      let params: any = {}

      params = {
        group: action,
        ...Object.keys(this.filters).reduce(
          (acc, filterKey) => {
            let data = this.filters[filterKey]
            if (data) {
              if (Array.isArray(data)) {
                data = this.getRequestCode(filterKey, data)
              }
              acc[filterKey === 'ordersQ' ? 'q' : filterKey] = data as string
            }
            return acc
          },
          {} as { [key: string]: string },
        ),
      }

      const { data } = await OrdersService.getOrdersInWork({
        params,
      })

      runInAction(() => {
        this[storeOrdersInWorkActions[action]] = data
      })
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      this.closeLoader(action)
    }
  }

  getAvailableProductOptions = async (id: string) => {
    try {
      this.activeLoader('availabilitesProductOptions')
      const { data } = await OrdersService.getAvailableProductOptions(id)
      runInAction(() => {
        this.availabilitesProductOptions = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('availabilitesProductOptions')
      })
    }
  }

  getAttachments = async (id: string) => {
    try {
      runInAction(() => {
        this.attachments = []
      })
      const { data } = await OrdersService.getAttachments(id)
      runInAction(() => {
        this.attachments = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getOrderById = async (id: string) => {
    this.activeLoader('orders')

    try {
      const { data } = await OrdersService.getOrderById(id)
      const { products, stampProducts, ...orderData } = data

      runInAction(() => {
        this.currentOrder = orderData
        this.items = orderData.items
        this.digitalProducts = orderData.digitalProducts
        this.currentOrderProducts = products as ProductsArray
        this.currentOrderStampProducts = stampProducts || []
      })
      return data
    } catch (error) {
      this.ordersError = true
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      this.closeLoader('orders')
    }
  }

  getRequestCode = (storeKey: string, selectedFilterByStoreKey: string[]) => {
    const storeData =
      store.globalStore.filtersBySelect[storeKey as keyof FiltersBySelect] ||
      this[storeKey]
    const selectedToLowerCase = selectedFilterByStoreKey.map((e) =>
      e.toLowerCase(),
    )
    const result = storeData
      ? storeData
          .map((e) => ({ ...e, name: e.name.toLowerCase() }))
          .filter((elem: any) => selectedToLowerCase.includes(elem.name))
          .map((e: any) => e.code || e.id || e.name)
      : selectedFilterByStoreKey
    return result
  }

  loadOrders = async (action: 'resetOrders' | 'loadMoreOrders') => {
    if (
      action === 'resetOrders'
        ? false
        : this.requestSettings.isAllLoaded ||
          !this.orders ||
          this.ordersError ||
          this.changedTab === 'atWork' ||
          this.loaders.resetOrders ||
          this.loaders.loadMoreOrders
    )
      return

    this.ordersAbortController?.abort()
    const abortController = new AbortController()
    this.ordersAbortController = abortController

    const loader: keyof Loaders = action
    this.activeLoader(loader)
    try {
      const params = Object.values(this.filters).some((e) => e?.length)
        ? Object.entries(this.filters).reduce(
            (acc, [filterKey, value]) => {
              let data = value
              if (data) {
                if (Array.isArray(data) && filterKey !== 'products') {
                  data = this.getRequestCode(filterKey, data)
                }
                acc[filterKey === 'ordersQ' ? 'q' : filterKey] = data as string
              }
              return acc
            },
            {} as { [key: string]: string },
          )
        : {}
      const { data } = await OrdersService.getOrders({
        signal: abortController.signal,
        params: {
          limit: this.requestSettings.limit,
          offset: action === 'resetOrders' ? 0 : this.requestSettings.offset,
          ...params,
        },
      })

      if (action === 'loadMoreOrders') {
        runInAction(() => {
          this.orders = [...(this.orders ?? []), ...data]
        })
      } else {
        runInAction(() => {
          this.orders = data
        })
      }

      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: data.length < this.requestSettings.limit,
        offset:
          action === 'resetOrders'
            ? 20
            : this.requestSettings.offset + this.requestSettings.limit,
      }
    } catch (error) {
      if (axios.isCancel(error)) return
      throw new Error(handleAxiosError(error))
    } finally {
      const isCurrentRequest = this.ordersAbortController === abortController

      if (action === 'loadMoreOrders' || isCurrentRequest) {
        this.closeLoader(loader)
      }
      if (isCurrentRequest) {
        this.ordersAbortController = null
      }
    }
  }

  getFilters = async () => {
    return Promise.all([this.getWarehouses()])
      .then((res) => {
        runInAction(() => {
          const [warehouses] = res
          if (warehouses) {
            this.warehouses = warehouses
          }
        })
      })
      .finally(() => {
        runInAction(() => {
          this.loaders.loadedAllFilters = true
        })
      })
  }

  getWarehouses = async () => {
    try {
      const { data } = await RetailService.getWarehouses()
      runInAction(() => {
        this.warehouses = data
      })
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }
}
